import { closeX } from './popup.js';
import { $, el } from '../state/store.js';
import { SHAPES } from '../core/doc.js';
import { addObject, addShape } from '../scene/objects.js';
import { ICONS, SHAPE_ICONS } from './controls.js';

/* ADD 3D MODEL — the palette's 3D Model button asks what kind before doing
   anything: bring in a model file, or drop in a basic shape.

   It used to open the file picker straight away, which left somebody who only
   wanted a cube to stand a product on, or a sphere to block in a composition,
   with nothing to pick. The import is still the first thing in the dialog, so
   the old path is one click longer and no harder to find.

   Same overlay and card as Inspiration (ui/templates.js) and the other
   dialogs, so it looks like part of the app. Unlike most of them it also
   closes on Escape and on a click outside the card: it is a picker, and a
   picker should be as easy to leave as it was to open. */

let overlay = null;
function ensureOverlay() {
  if (overlay) return overlay;
  overlay = el('div', 'authwrap hidden');
  overlay.id = 'addmodelwrap';
  /* Only the backdrop itself: a click that starts on a tile and ends outside
     it must not count as a click on the backdrop. */
  overlay.addEventListener('pointerdown', ev => { overlay.__down = ev.target === overlay; });
  overlay.addEventListener('click', ev => { if (ev.target === overlay && overlay.__down) hide(); });
  document.body.appendChild(overlay);
  return overlay;
}

const onKey = ev => { if (ev.key === 'Escape') { ev.stopPropagation(); hide(); } };
function hide() {
  ensureOverlay().classList.add('hidden');
  removeEventListener('keydown', onKey, true);
}

const icon = (paths, cls) => `<svg class="${cls}" viewBox="0 0 16 16" aria-hidden="true">${paths}</svg>`;

export function showAddModel() {
  const w = ensureOverlay();
  w.innerHTML = '';
  const card = el('div', 'authcard authcard--wide addmodel');
  card.setAttribute('role', 'dialog');
  card.setAttribute('aria-modal', 'true');
  card.setAttribute('aria-label', 'Add a 3D model');
  card.appendChild(el('h2', null, 'Add a 3D model'));
  closeX(card, hide);

  /* The file first: a real product model is what most pieces are built
     around, and this is the path the button took before it opened a dialog. */
  const imp = el('button', 'addmodel__import');
  imp.type = 'button';
  imp.innerHTML = icon(ICONS.model, 'addmodel__importicon') +
    '<span class="addmodel__importtext"><b>Import a 3D model</b>' +
    '<span>A .glb file, with its own materials and textures</span></span>';
  imp.onclick = () => { hide(); $('#fileGlb').click(); };
  card.appendChild(imp);

  card.appendChild(el('div', 'addmodel__label', 'Basic shapes'));
  const grid = el('div', 'addmodel__grid');
  for (const [kind, s] of Object.entries(SHAPES)) {
    const b = el('button', 'addmodel__tile');
    b.type = 'button';
    b.dataset.shape = kind;
    b.innerHTML = icon(SHAPE_ICONS[kind], 'addmodel__tileicon') + `<span>${s.label}</span>`;
    b.onclick = () => { hide(); addShape(kind); };
    grid.appendChild(b);
  }
  card.appendChild(grid);

  /* Not a shape and not a model: nothing of it renders. It sits here because
     this is where 3D things are added, and a null is the one tool for moving
     several of them as one. */
  card.appendChild(el('div', 'addmodel__label', 'Helpers'));
  const helpers = el('div', 'addmodel__grid');
  const nb = el('button', 'addmodel__tile');
  nb.type = 'button';
  nb.dataset.shape = 'null';
  nb.title = 'A transform with nothing in it. Link objects to it, then move, turn or scale the null to carry them — it never appears in an export.';
  nb.innerHTML = icon(ICONS.null, 'addmodel__tileicon') + '<span>Null object</span>';
  nb.onclick = () => { hide(); addObject('null'); };
  helpers.appendChild(nb);
  card.appendChild(helpers);
  card.appendChild(el('p', 'authnote addmodel__note',
    'Shapes take their colour, finish and size from the inspector, so they can stand in for a product until the real model is ready, or be the piece themselves.'));

  const row = el('div', 'authrow');
  const close = el('button', 'btn btn--gh', 'Close');
  close.onclick = hide;
  row.appendChild(close);
  card.appendChild(row);

  w.appendChild(card);
  w.classList.remove('hidden');
  addEventListener('keydown', onKey, true);
  imp.focus();
}
